import { useEffect, useRef } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import aimsTheme from "../../../../meridian-theme-aims.json";
import MentionCard from "./MentionCard";
import SectionCard from "./SectionCard";
import { parseStreamingText } from "./streamingCardParser";

const CARD_RENDERERS = {
  mention: (card, key) => (
    <MentionCard key={key} name={card.data.name || ""} time={card.data.time || ""} context={card.data.context || ""} detail={card.data.detail || ""} />
  ),
  section: (card, key) => (
    <SectionCard key={key} type={card.data.type || "topics"} title={card.data.title || ""} content={card.data.content || []} />
  ),
};

const renderBoldText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const NotEnoughTranscriptMessage = () => (
  <Column spacing="300" spacingInset="300 0">
    <Text fontFamily="amazonEmber" type="b200">
      <strong>You're right on time!</strong>
    </Text>
    <Text fontFamily="amazonEmber" type="b200">
      Not enough of the meeting has happened yet.<br/>Jump in — you haven't missed much!
    </Text>
    <Text fontFamily="amazonEmber" type="b200">
      What else can I help during this meeting?
    </Text>
  </Column>
);

const NameNotMentionedMessage = () => (
  <Column spacing="300" spacingInset="300 0">
    <Text fontFamily="amazonEmber" type="b200">
      <strong>Your name didn't come up during the meeting.</strong>
    </Text>
    <Text fontFamily="amazonEmber" type="b200">
      Want to see what's been discussed so far?
    </Text>
  </Column>
);

const AgentMessage = ({ text }) => {
  if (text.trim() === "not_enough_transcript") {
    return <NotEnoughTranscriptMessage />;
  }
  if (text.trim() === "name_not_mentioned") {
    return <NameNotMentionedMessage />;
  }

  const { plainText, completedCards, streamingCard } = parseStreamingText(text);

  return (
    <Column spacing="200" spacingInset="300 0">
      {plainText && plainText.split("\n").map((line, li) => {
        const trimmed = line.trim();
        if (!trimmed) return <Row key={li} height="8px" />;
        return (
          <Text key={li} fontFamily="amazonEmber" type="b200">
            {renderBoldText(trimmed)}
          </Text>
        );
      })}
      {completedCards.length > 0 && (
        <Column spacing="400" spacingInset="200 0 0 0">
          {completedCards.map((card, i) => {
            const renderer = CARD_RENDERERS[card.cardType];
            return renderer ? renderer(card, `card-${i}`) : null;
          })}
        </Column>
      )}
      {streamingCard && (
        <Column spacing="400" spacingInset="200 0 0 0">
          {CARD_RENDERERS[streamingCard.cardType]?.(streamingCard, "streaming")}
        </Column>
      )}
    </Column>
  );
};

const ChatMessages = ({ messages, isLoading }) => {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  return (
    <Column
      spacing="none"
      width="318px"
      overflowY="auto"
      spacingInset="400 0"
      
    >
      {messages.map((msg, idx) => {
        if (!msg.text) return null;
        return (
          <Column key={idx} spacing="none">
            {msg.role === "user" ? (
              <Row alignmentHorizontal="end" spacing="none">
                <Theme tokens={{ boxBorderRadiusFill: 8, boxBorderWidth: 0 }}>
                  <Box
                    type="fill"
                    backgroundColor="#D2F3E8"
                    spacingInset="350 400 350 400"
                    maxWidth="250px"
                  >
                    <Text fontFamily="amazonEmber" type="b200">{msg.text}</Text>
                  </Box>
                </Theme>
              </Row>
            ) : (
              <AgentMessage text={msg.text} />
            )}
            <Row height="16px" />
          </Column>
        );
      })}

      {isLoading && (
        <Row alignmentHorizontal="left" spacing="none">
          <Box spacingInset="300">
            <Theme palette={aimsTheme}>
              <Loader size="small" />
            </Theme>
          </Box>
        </Row>
      )}

      <Row ref={bottomRef} height="1px" />
    </Column>
  );
};

export default ChatMessages;
